import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// Build-time backend URL (set in .env at build time, points to preview)
const BUILD_TIME_API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Resolve the backend URL at RUNTIME.
 * - Production (notariapublica90.com): use same-origin relative paths so the request goes
 *   to /api/* on notariapublica90.com (where the backend is also deployed).
 * - Preview/local: use the build-time REACT_APP_BACKEND_URL.
 *
 * This is critical because REACT_APP_* env vars are baked at build time, so a build
 * made for preview would otherwise call the preview backend even when served from prod.
 */
function resolveApiUrl() {
  if (typeof window === 'undefined') return BUILD_TIME_API_URL;
  const host = window.location.hostname;
  // Production domain → same-origin
  if (host === 'notariapublica90.com' || host.endsWith('.notariapublica90.com')) {
    return ''; // empty = relative URL, browser uses current origin
  }
  return BUILD_TIME_API_URL;
}

const API_URL = resolveApiUrl();
const TOKEN_STORAGE_KEY = 'notaria90_access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'notaria90_refresh_token';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Algo salió mal. Por favor intenta de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (err) {
    console.warn('[auth] localStorage read failed:', err?.message || err);
    return null;
  }
}

function saveTokens(accessToken, refreshToken) {
  try {
    if (accessToken) localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
  } catch (err) {
    // localStorage might be blocked (private mode); auth still works via cookies.
    console.warn('[auth] localStorage write failed:', err?.message || err);
  }
}

function clearTokens() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch (err) {
    console.warn('[auth] localStorage clear failed:', err?.message || err);
  }
}

// Build axios config with both cookies (web) and Bearer header (cross-browser fallback)
function authConfig() {
  const token = getStoredToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
}

function readErrorMessage(error) {
  // Network / CORS errors don't have a response
  if (!error.response) {
    if (error.code === 'ERR_NETWORK') {
      return 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
    }
    return error.message || 'Error de red. Intenta de nuevo.';
  }
  return (
    formatApiErrorDetail(error.response?.data?.detail) ||
    error.message ||
    'Algo salió mal. Por favor intenta de nuevo.'
  );
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, authConfig());
      setUser(data);
    } catch (error) {
      // Not authenticated — clear any stale tokens.
      if (error?.response?.status !== 401) {
        console.warn('[auth] checkAuth failed:', error?.message || error);
      }
      clearTokens();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const register = async (name, email, phone, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { name, email, phone, password },
        { withCredentials: true }
      );
      // Save tokens as fallback for Safari/Brave/private browsing
      if (data?.access_token) {
        saveTokens(data.access_token, data.refresh_token);
      }
      setUser(data.user || data);
      return { success: true };
    } catch (error) {
      return { success: false, error: readErrorMessage(error) };
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (data?.access_token) {
        saveTokens(data.access_token, data.refresh_token);
      }
      setUser(data.user || data);
      return { success: true };
    } catch (error) {
      return { success: false, error: readErrorMessage(error) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, authConfig());
    } catch (error) {
      // Even if logout fails on the server, we still clear local state below.
      console.warn('[auth] logout request failed:', error?.message || error);
    }
    clearTokens();
    setUser(false);
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    authConfig, // expose for other API calls
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
